import { writable, get } from 'svelte/store'

export interface PostingPromptData {
  prompt: string
  modelId?: string // 포스팅별 모델 설정
  lastUpdated: Date
}

export interface PostingPromptState {
  data: PostingPromptData
  isVisible: boolean
}

// 탭별 포스팅 프롬프트를 저장하는 맵
const postingPromptsMap = new Map<string, PostingPromptData>()

// 현재 활성 탭의 프롬프트를 관리하는 스토어
function createPostingPromptStore() {
  const { subscribe, set, update } = writable<PostingPromptState>({
    data: {
      prompt: '',
      modelId: undefined,
      lastUpdated: new Date()
    },
    isVisible: false
  })

  let currentTabId: string | null = null

  return {
    subscribe,
    
    // 탭 전환 시 프롬프트 로드
    loadForTab(tabId: string) {
      currentTabId = tabId
      const savedPrompt = postingPromptsMap.get(tabId)
      if (savedPrompt) {
        update(state => ({
          ...state,
          data: savedPrompt
        }))
      } else {
        update(state => ({
          ...state,
          data: {
            prompt: '',
            modelId: undefined,
            lastUpdated: new Date()
          }
        }))
      }
    },

    // 현재 탭의 프롬프트 저장
    save(prompt: string, modelId?: string) {
      if (!currentTabId) return
      
      const data: PostingPromptData = {
        prompt,
        modelId,
        lastUpdated: new Date()
      }
      
      postingPromptsMap.set(currentTabId, data)
      update(state => ({
        ...state,
        data
      }))
    },

    // 프롬프트 텍스트만 업데이트
    updatePrompt(prompt: string) {
      if (!currentTabId) return
      
      const current = postingPromptsMap.get(currentTabId) || {
        prompt: '',
        modelId: undefined,
        lastUpdated: new Date()
      }
      
      const updated = {
        ...current,
        prompt,
        lastUpdated: new Date()
      }
      
      postingPromptsMap.set(currentTabId, updated)
      update(state => ({
        ...state,
        data: updated
      }))
    },

    // 모델 ID만 업데이트
    updateModel(modelId: string) {
      if (!currentTabId) return
      
      const current = postingPromptsMap.get(currentTabId) || {
        prompt: '',
        modelId: undefined,
        lastUpdated: new Date()
      }
      
      const updated = {
        ...current,
        modelId: modelId || undefined,
        lastUpdated: new Date()
      }
      
      postingPromptsMap.set(currentTabId, updated)
      update(state => ({
        ...state,
        data: updated
      }))
    },

    // 현재 탭의 프롬프트 가져오기
    get(): PostingPromptData {
      if (!currentTabId) {
        return {
          prompt: '',
          modelId: undefined,
          lastUpdated: new Date()
        }
      }
      return postingPromptsMap.get(currentTabId) || {
        prompt: '',
        modelId: undefined,
        lastUpdated: new Date()
      }
    },

    // 프롬프트가 있는지 확인
    hasPrompt(): boolean {
      if (!currentTabId) return false
      const data = postingPromptsMap.get(currentTabId)
      return !!(data && data.prompt && data.prompt.trim())
    },

    // 탭 삭제 시 프롬프트도 삭제
    removeForTab(tabId: string) {
      postingPromptsMap.delete(tabId)
      if (currentTabId === tabId) {
        currentTabId = null
        update(state => ({
          ...state,
          data: {
            prompt: '',
            modelId: undefined,
            lastUpdated: new Date()
          }
        }))
      }
    },

    // 패널 가시성 관리
    toggleVisibility() {
      update(state => ({
        ...state,
        isVisible: !state.isVisible
      }))
    },

    setVisibility(visible: boolean) {
      update(state => ({
        ...state,
        isVisible: visible
      }))
    },

    getVisibility(): boolean {
      return get({ subscribe }).isVisible
    },
    
    hide() {
      update(state => ({
        ...state,
        isVisible: false
      }))
    },

    // 모든 포스팅 프롬프트 초기화
    clear() {
      postingPromptsMap.clear()
      set({
        data: {
          prompt: '',
          modelId: undefined,
          lastUpdated: new Date()
        },
        isVisible: false
      })
    }
  }
}

export const postingPromptStore = createPostingPromptStore()